import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { Artist } from '@/features/clubs/types/types';
import { getSignedMediaUrl } from '@/lib/utils';
import { likeService } from '@/features/likes/services/likeService';
import LikeButton from '@/components/LikeButton';

// Image placeholder component
const ImagePlaceholder = ({ label, className }: { label: string; className?: string }) => (
  <div className={`flex items-center justify-center bg-gray-800 text-gray-500 rounded-2xl w-full h-full ${className}`}>
    <span className="text-sm tracking-widest uppercase">{label}</span>
  </div>
);

// Artist Card Component
const ArtistCard = ({ artist, onArtistClick, isLiked, onToggleLike }: { 
  artist: Artist; 
  onArtistClick: (artistId: string) => void;
  isLiked?: boolean;
  onToggleLike?: (newState: boolean) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const mediaVariants = {
    initial: { opacity: 0, scale: 0.9, y: 10 },
    animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" as const } },
    exit: { opacity: 0, scale: 0.9, y: -10, transition: { duration: 0.3, ease: "easeIn" as const } },
  };

  // Get signed URLs for image and video
  const imageUrl = getSignedMediaUrl(artist.card_image_id, 'image').url;
  const videoUrl = getSignedMediaUrl(artist.card_video_id, 'video').url;

  const handleClick = () => {
    onArtistClick(artist.id);
  };

  return (
    <motion.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      className="group relative flex flex-col bg-gray-900/50 border border-gray-800 rounded-3xl overflow-hidden transition-all duration-300 hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-900/20 h-full cursor-pointer transform-gpu"
      style={{ transformOrigin: "center" }}
    >
      <div className="relative w-full h-64 overflow-hidden flex-shrink-0">
        <AnimatePresence initial={false}>
          {!isHovered ? (
            <motion.div key="image" {...mediaVariants} className="absolute inset-0">
              {!imageError ? (
                <img
                  src={imageUrl}
                  alt={artist.name}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <ImagePlaceholder label={`${artist.name} Image`} />
              )}
            </motion.div>
          ) : (
            <motion.div key="video" {...mediaVariants} className="absolute inset-0">
              {!videoError ? (
                <video
                  src={videoUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  onError={() => setVideoError(true)}
                />
              ) : (
                <ImagePlaceholder label={`${artist.name} Video`} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Like Button - Top Right */}
        {onToggleLike && (
          <LikeButton
            entityId={artist.id}
            entityType="artists"
            isLiked={isLiked || false}
            onToggle={onToggleLike}
            className="absolute top-3 right-3 z-10"
            size="md"
          />
        )}
      </div>
      <div className="flex flex-col flex-grow p-6 min-h-0">
        <h3 className="text-2xl font-serif font-medium text-white mb-4">{artist.name}</h3>
        
        <p className="text-gray-400 text-sm leading-relaxed mb-4 flex-grow overflow-hidden" style={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical'
        }}>{artist.description}</p>

        {/* Music Genres */}
        <div className="flex flex-wrap gap-2 mt-auto">
          {artist.music_genres?.map(genre => (
            <span key={genre} className="px-3 py-1 bg-gray-800 text-gray-300 text-xs font-medium rounded-full">
              {genre}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Artists Section Component
interface ArtistsSectionProps {
  artists: Artist[];
  clubHandle?: string;
}

const ArtistsSection: React.FC<ArtistsSectionProps> = ({ artists, clubHandle }) => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down');
  const [lastScrollY, setLastScrollY] = useState(0);
  const [likedMap, setLikedMap] = useState<Record<string, boolean>>({});

  // Track scroll direction
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleArtistClick = (artistId: string) => {
    if (clubHandle) {
      navigate(`/venues/${clubHandle}/artist/${artistId}`);
    } else {
      // Try to get handle from artist data
      const artist = artists.find(a => a.id === artistId);
      if (artist?.business?.handle) {
        navigate(`/venues/${artist.business.handle}/artist/${artistId}`);
      }
    }
  };

  // Fetch like status for artists on initial load when user is authenticated
  // Note: Intentionally not re-fetching when isAuthenticated changes mid-session
  // to avoid race conditions with toggle operations
  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!isAuthenticated || !artists || artists.length === 0) return;
      
      const validArtistIds = artists
        .filter(artist => artist && artist.id)
        .map(artist => artist.id);
      
      if (validArtistIds.length === 0) return;
      
      const response = await likeService.checkLikes({
        entity_ids: validArtistIds,
        entity_type: 'artists',
      });
      setLikedMap(response.liked_map);
    };

    fetchLikeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artists]);

  const handleLikeToggle = (artistId: string, newLikedState: boolean) => {
    setLikedMap(prev => ({ ...prev, [artistId]: newLikedState }));
  };

  // Group artists into rows (2 artists per row)
  const artistsInRows = [];
  for (let i = 0; i < artists.length; i += 2) {
    artistsInRows.push(artists.slice(i, i + 2));
  }

  // Dynamic variants based on scroll direction
  const getGridContainerVariants = () => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.4, 
        delayChildren: 0.1,
        staggerDirection: scrollDirection === 'up' ? -1 : 1
      },
    },
  });

  const rowVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0, delayChildren: 0 },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: scrollDirection === 'up' ? -30 : 30, 
      scale: 0.8 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.6, 
        ease: "easeOut" as const,
        scale: { duration: 0.5, ease: "easeOut" as const }
      } 
    },
  };

  // Don't render if no artists or filter out null/undefined artists
  if (!artists || artists.length === 0) {
    return null;
  }

  // Filter out any null, undefined, or invalid artists
  const validArtists = artists.filter(artist => artist && artist.id && artist.name);

  // Don't render if no valid artists after filtering
  if (validArtists.length === 0) {
    return null;
  }

  return (
    <section id="artists" ref={sectionRef} className="relative bg-black py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-4xl sm:text-5xl font-serif font-medium text-white">Featured Artists</h2>
        </div>
        {/* Row-based animation layout */}
        <motion.div 
          className="space-y-16"
          variants={getGridContainerVariants()}
          initial="hidden"
          // Animate when the component scrolls into view
          whileInView="visible"
          viewport={{ once: false, amount: 0.1 }}
        >
          {artistsInRows.map((row, rowIndex) => (
            <motion.div 
              key={rowIndex}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8"
              variants={rowVariants}
            >
              {row.map(artist => (
                <motion.div key={artist.id} variants={itemVariants}>
                  <ArtistCard 
                    artist={artist} 
                    onArtistClick={handleArtistClick}
                    isLiked={likedMap[artist.id] || false}
                    onToggleLike={(newState) => handleLikeToggle(artist.id, newState)}
                  />
                </motion.div>
              ))}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ArtistsSection;
